import IResult from "../../models/result/IResult";
import OnboardingProgressModel from "../../models/onboarding/api/OnboardingProgressModel";
import { sendGet, sendGetWithCustomHeaders, sendPost, sendPostWithCustomHeaders, sendPut, sendPutWithCustomHeaders } from "./generic";

export type OnboardingEmailTriggerId =
  | "org-launched"
  | "org-reminder"
  | "telephony-reminder"
  | "org-submitted"
  | "loc-launched"
  | "loc-reminder"
  | "loc-submitted"
  | "testing-ready"
  | "phase1-feedback-submitted"
  | "phase2-testing-ready"
  | "phase2-feedback-submitted"
  | "launch-ready"
  | "go-live-reminder"
  | "go-live"
  | "outbound-launched"
  | "outbound-reminder"
  | "outbound-submitted";

export type OnboardingEmailTriggerStatus = "Pending" | "Sent" | "Skipped";

export interface OnboardingEmailTriggerState {
  firstSentAt?: string; // ISO UTC, first-ever sent/manual timestamp
  sentAt?: string; // ISO UTC, latest sent/manual timestamp
  sentManually?: boolean;
  status: OnboardingEmailTriggerStatus;
  recipientEmail?: string;
  recipientFirstName?: string;
  subject?: string;
  body?: string; // HTML
  sendCount?: number;
}

export interface PreviewOnboardingEmailRequest {
  recipientFirstName?: string;
  recipientEmail?: string;
  manual?: boolean;
  onboardingBaseUrl: string; // required
}

export interface PreviewOnboardingEmailResponse {
  applicationId: string;
  triggerId: OnboardingEmailTriggerId;
  fromEmail: "support@voicify.com";
  recipientFirstName?: string;
  recipientEmail?: string;
  subject: ""; // intentionally empty
  body: ""; // intentionally empty
  link: string;
}

export interface SendOnboardingEmailRequest {
  recipientFirstName?: string;
  recipientEmail?: string; // semicolon-delimited list supported
  subject: string;
  body: string; // HTML
  manual: boolean;
  resend?: boolean; // new
}

export interface UpdateOnboardingEmailTriggerStatusRequest {
  action: "skip" | "reset";
}

export interface BulkUpdateOnboardingEmailTriggerStatusRequest {
  triggerIds: OnboardingEmailTriggerId[];
  action: "skip" | "reset";
}

export interface CompleteOnboardingFieldRequest {
    associatedVariable: string;
    value: string;
}

export interface CompleteOnboardingProgressRequest {
    currentStep: string;
    percentComplete: number;
    detailsJson: string;
}

export interface CompleteOnboardingRequest {
    locationTemplateConfigurationId: string;
    fields: CompleteOnboardingFieldRequest[];
    progress: CompleteOnboardingProgressRequest;
}

export interface CompleteOnboardingResponse {
    status: "completed";
    attemptId: string;
    affectedLocationCount: number;
    preservedExistingValueCount: number;
    skippedLocations: string[];
    completedAt: string;
}

export const generateOnboardingLink = async (applicationId: string): Promise<IResult<string>> => {
    const result = await sendPost<string>(`/Onboarding/application/${applicationId}/link`, {});

    if (result.resultType === "Ok" && typeof result.data === "string") {
        try {
            const parsed = JSON.parse(result.data);
            if (typeof parsed === "string") {
                result.data = parsed;
            }
        } catch (_) {
        }
    }

    return result;
};

export const getOnboardingProgress = async (baseUrl?: string): Promise<IResult<OnboardingProgressModel>> => {
    return await sendGet<OnboardingProgressModel>("/Onboarding/progress", baseUrl);
};

export const getOnboardingProgressWithApiKey = async (apiKey: string, baseUrl?: string): Promise<IResult<OnboardingProgressModel>> => {
    return await sendGetWithCustomHeaders<OnboardingProgressModel>("/Onboarding/progress", { 'X-Api-Key': apiKey }, baseUrl);
};

export const getOnboardingProgressForApplication = async (applicationId: string): Promise<IResult<OnboardingProgressModel>> => {
    return await sendGet<OnboardingProgressModel>(`/Onboarding/application/${applicationId}/progress`);
};

export const updateOnboardingProgress = async (model: OnboardingProgressModel, baseUrl?: string): Promise<IResult<OnboardingProgressModel>> => {
    return await sendPut<OnboardingProgressModel>("/Onboarding/progress", model, baseUrl);
};

export const updateOnboardingProgressWithApiKey = async (model: OnboardingProgressModel, apiKey: string, baseUrl?: string): Promise<IResult<OnboardingProgressModel>> => {
    return await sendPutWithCustomHeaders<OnboardingProgressModel>("/Onboarding/progress", model, { 'X-Api-Key': apiKey }, baseUrl);
};

export const completeOnboardingWithApiKey = async (
    sharedApplicationId: string,
    model: CompleteOnboardingRequest,
    apiKey: string,
    idempotencyKey: string,
    baseUrl?: string
): Promise<IResult<CompleteOnboardingResponse>> => {
    return await sendPostWithCustomHeaders<CompleteOnboardingResponse>(
        `/Onboarding/application/${sharedApplicationId}/complete`,
        model,
        {
            'X-Api-Key': apiKey,
            'Idempotency-Key': idempotencyKey
        },
        baseUrl
    );
};

export const previewOnboardingEmail = async (
    applicationId: string,
    triggerId: OnboardingEmailTriggerId,
    request: PreviewOnboardingEmailRequest,
    baseUrl?: string
): Promise<IResult<PreviewOnboardingEmailResponse>> => {
    return await sendPost<PreviewOnboardingEmailResponse>(`/Onboarding/application/${applicationId}/emailTriggers/${triggerId}/preview`, request, baseUrl);
};

export const sendOnboardingEmail = async (
    applicationId: string,
    triggerId: OnboardingEmailTriggerId,
    request: SendOnboardingEmailRequest,
    baseUrl?: string
): Promise<IResult<OnboardingProgressModel>> => {
    return await sendPost<OnboardingProgressModel>(`/Onboarding/application/${applicationId}/emailTriggers/${triggerId}/send`, request, baseUrl);
};

export const updateOnboardingEmailTriggerStatus = async (
    applicationId: string,
    triggerId: OnboardingEmailTriggerId,
    request: UpdateOnboardingEmailTriggerStatusRequest,
    baseUrl?: string
): Promise<IResult<OnboardingProgressModel>> => {
    return await sendPut<OnboardingProgressModel>(`/Onboarding/application/${applicationId}/emailTriggers/${triggerId}/status`, request, baseUrl);
};

export const bulkUpdateOnboardingEmailTriggerStatus = async (
    applicationId: string,
    request: BulkUpdateOnboardingEmailTriggerStatusRequest,
    baseUrl?: string
): Promise<IResult<OnboardingProgressModel>> => {
    return await sendPut<OnboardingProgressModel>(`/Onboarding/application/${applicationId}/emailTriggers/status`, request, baseUrl);
};
